import React, {Fragment, useContext} from "react";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {LeaveWithoutSavingWarningUtility} from "../common/LeaveWithoutSavingWarningUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {ApiUtility} from "../common/ApiUtility";
import {GlobalContext} from "../admin-frame/AdminFrame";
import {PillButton} from "../common/PillButton";
import {InvoiceSyncStatusBreakdown} from "./InvoiceSyncStatusBreakdown";
import {EnvironmentUtility} from "../common/EnvironmentUtility";
import {SubscriptionInvoiceLinesDetail} from "../subscriptions/SubscriptionInvoiceLinesDetail";
import {Constants} from "../common/Constants";
import {CollapsibleInvoiceFpDataDisplay} from "./CollapsibleInvoiceFpDataDisplay";
import {Modal} from "../common/Modal";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";

export let UpdateTrackedInvoice = function (props) {
    const context = useContext(GlobalContext);
    let renderableData = useRenderableData({
        showConfirmCancelModal: false,
        loading: false
    })

    function onClickCancelInvoice() {
        renderableData.updateAndRender({showConfirmCancelModal: true})
    }

    function closeConfirmCancelModal() {
        renderableData.updateAndRender({showConfirmCancelModal: false})
    }

    function onClickSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncInvoice(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully synced invoice.`);
                    props.afterSuccessfulSave(props.invoice.id)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the invoice.`);
            });
    }

    function performCancelInvoice() {
        renderableData.updateAndRender({
            loading: true,
            showConfirmCancelModal: false
        })
        ApiUtility.cancelInvoice(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully canceled invoice.`);
                    props.afterSuccessfulCancel(props.invoice.id)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while canceling the invoice.`);
            });
    }

    function addTestFullStripeInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestFullStripeInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully added a full test Stripe payment for invoice.`);
                    props.afterSuccessfulSave(props.invoice.id)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a full test Stripe payment for the invoice.`);
            });
    }

    function addTestFullQboInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestFullQboInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully added a full test QBO payment for invoice.`);
                    props.afterSuccessfulSave(props.invoice.id)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a full test QBO payment for the invoice.`);
            });
    }

    function addTestPartialQboInvoicePayment() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.addTestPartialQboInvoicePayment(props.invoice.id)
            .then((response) => {
                LeaveWithoutSavingWarningUtility.disableLeaveWithoutSavingWarnings(context);
                window.setTimeout(function () {
                    MessageDisplayerUtility.success(`Successfully added a partial test QBO payment for invoice.`);
                    props.afterSuccessfulSave(props.invoice.id)
                }, 0)
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while adding a partial test QBO payment for the invoice.`);
            });
    }

    let invoicesInSync = (
        props.unsyncedData.extStripePaymentBts.length === 0 &&
        props.unsyncedData.qboPayments.length === 0 &&
        props.unsyncedData.stripePayouts.length === 0 &&
        props.unsyncedData.stripePayoutBts.length === 0
    );
    let needsSyncDetails = '';
    if (props.unsyncedData.extStripePaymentBts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe invoice payment'
        if (props.unsyncedData.extStripePaymentBts.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.qboPayments.length > 0) {
        needsSyncDetails = 'Found unsynced QBO invoice payment'
        if (props.unsyncedData.qboPayments.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.stripePayouts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe payout'
        if (props.unsyncedData.stripePayouts.length > 1) {
            needsSyncDetails += 's'
        }
    }
    if (props.unsyncedData.stripePayoutBts.length > 0) {
        needsSyncDetails = 'Found unsynced Stripe payout balance transaction'
        if (props.unsyncedData.stripePayoutBts.length > 1) {
            needsSyncDetails += 's'
        }
    }

    let modalProps = props.modalProps
    let stripeInvoiceCancelable = (
        props.stripeInvoice &&
        props.stripeInvoice.amount_paid === 0 &&
        (
            props.stripeInvoice.status === Constants.externalStripeInvoiceStatuses.draft ||
            props.stripeInvoice.status === Constants.externalStripeInvoiceStatuses.open
        )
    )
    if (
        props.qboInvoice &&
        (
            stripeInvoiceCancelable ||
            props.stripeInvoice?.status === Constants.externalStripeInvoiceStatuses.void ||
            !props.stripeInvoice
        ) &&
        props.qboInvoice.Payments.length === 0 &&
        props.qboInvoice.Balance === props.qboInvoice.TotalAmt
    ) {
        modalProps.additionalOptionsRenderer = () => (
            <PillButton
                onClick={onClickCancelInvoice}
                buttonText="CANCEL INVOICE"
                containerClasses="cancel-button-container"
                buttonClasses="cancel-button"
            />
        )
    }

    return (
        <Fragment>
            <Modal {...props.modalProps}>
                <div className="single-invoice">
                    <div className="single-invoice-content">
                        <ConditionalRenderer if={renderableData.loading} renderer={() => (
                            <OverlayLoadingIndicator/>
                        )}/>
                        <div className="invoice-summary">
                            <div className="test-invoice-id">{props.invoice.id}</div>
                            <div
                                className="test-stripe-invoice-id"
                            >{props.stripeInvoice && props.stripeInvoice.id}</div>
                            <div className="test-qbo-invoice-id">{props.qboInvoice.Id}</div>
                            <div className="invoice-sync-status-section">
                                <div className="invoice-sync-status-summary">
                                    <div className="invoice-sync-status-summary-content">
                                        {(() => {
                                            if (invoicesInSync) {
                                                return (
                                                    <div className="sync-status-message invoice-in-sync-message">
                                                        <span>Invoices are in sync.</span>
                                                    </div>
                                                )
                                            } else {
                                                return (
                                                    <Fragment>
                                                        <div
                                                            className="sync-status-message invoice-out-of-sync-message"
                                                        >
                                                            <span>{`${needsSyncDetails} - invoices need to be synced.`}</span>
                                                        </div>
                                                        <PillButton
                                                            onClick={onClickSync}
                                                            containerClasses="sync-button-container invoice-sync-button-container"
                                                            buttonClasses="sync-button invoice-sync-button"
                                                            leftIconSrc={"/static/images/sync-white.svg"}
                                                            buttonText="SYNC"
                                                        />
                                                    </Fragment>
                                                )
                                            }
                                        })()}
                                    </div>
                                </div>
                                <InvoiceSyncStatusBreakdown
                                    stripeInvoice={props.stripeInvoice}
                                    qboInvoice={props.qboInvoice}
                                    supportedStripePaymentTypes={Constants.stripeFetchedPaymentMethodTypes}
                                    syncStatusData={props.syncStatusData}
                                />
                                <ConditionalRenderer
                                    if={
                                        EnvironmentUtility.inTestingEnvironment() &&
                                        props.invoice.invoice_status.name !== Constants.invoiceStatusNames.paid
                                    }
                                    renderer={() => (
                                        <div className="test-options-section">
                                            <div className="test-options">
                                                <div className="test-options-content">
                                                    <PillButton
                                                        onClick={addTestFullStripeInvoicePayment}
                                                        containerClasses="test-option-container"
                                                        buttonClasses="test-option-button test-full-stripe-payment-button"
                                                        buttonText="TEST ONLY: ADD FULL STRIPE PAYMENT"
                                                    />
                                                    <PillButton
                                                        onClick={addTestFullQboInvoicePayment}
                                                        containerClasses="test-option-container"
                                                        buttonClasses="test-option-button test-full-qbo-payment-button"
                                                        buttonText="TEST ONLY: ADD FULL QBO PAYMENT"
                                                    />
                                                    {/*
                                                todo uncomment when we add support for partial payments
                                                <PillButton
                                                    onClick={addTestPartialQboInvoicePayment}
                                                    containerClasses="test-option-container"
                                                    buttonClasses="test-option-button"
                                                    buttonText="TEST ONLY: ADD PARTIAL QBO PAYMENT"
                                                />
                                                */}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                        <ConditionalRenderer if={props.fpInvoice && props.fpInvoiceDataAnchor} renderer={() => (
                            <CollapsibleInvoiceFpDataDisplay
                                fpInvoiceDataAnchor={props.fpInvoiceDataAnchor}
                                fpInvoice={props.fpInvoice}
                            />
                        )}/>
                        <ConditionalRenderer if={props.invoice?.subscription} renderer={() => (
                            <div className="subscription-invoice-lines">
                                <div className="subscription-invoice-lines-header">
                                    Invoice Details
                                </div>
                                <SubscriptionInvoiceLinesDetail
                                    subscriptionInvoiceLines={props.invoice.subscription.subscription_invoice_lines}
                                />
                            </div>
                        )}/>
                    </div>
                </div>
            </Modal>
            <ConditionalRenderer if={renderableData.showConfirmCancelModal} renderer={() => (
                <ConfirmCancelModal
                    cancel={closeConfirmCancelModal}
                    confirm={performCancelInvoice}
                    headerText="Confirm Cancel Invoice"
                    cancelButtonText="NO - ABORT"
                    confirmButtonText="YES - CANCEL INVOICE"
                    warningMessageRenderer={() => {
                        let draftText = '';
                        let effectClass = 'void-effect';
                        let effectText = 'void'
                        if (
                            props.stripeInvoice &&
                            props.stripeInvoice.status === Constants.externalStripeInvoiceStatuses.draft
                        ) {
                            draftText = 'draft'
                            effectClass = 'delete-effect';
                            effectText = 'delete'
                        }

                        let stripeEffectMessage = null;
                        if (
                            props.stripeInvoice &&
                            props.stripeInvoice?.status !== Constants.externalStripeInvoiceStatuses.void
                        ) {
                            stripeEffectMessage = (
                                <li className="confirm-cancel-modal-sub-message stripe-effect">
                                    This will <span className={effectClass}>{effectText}</span> the
                                    Stripe {draftText} invoice.
                                </li>
                            )
                        }

                        let qboEffectMessage = (
                            <li className="confirm-cancel-modal-sub-message qbo-effect">
                                This will <span className="delete-effect">delete</span> the QBO invoice.
                            </li>
                        )
                        return (
                            <div>
                                <div className="confirm-cancel-modal-message">
                                    {`Are you sure you want to cancel this invoice?`}
                                </div>
                                <ul className="confirm-cancel-modal-message">
                                    {stripeEffectMessage}
                                    {qboEffectMessage}
                                </ul>
                            </div>
                        )
                    }}
                />
            )}/>
        </Fragment>
    );
}
